import type { VectorEdge, VectorNetwork, VectorVertex } from "@/document/model/vectorNetwork";
import { sampleEdgeBezier } from "@/document/operations/vectorGeometry";

type Point = { x: number; y: number };

export function planarizeVectorNetwork(network: VectorNetwork): VectorNetwork {
  const verticesById = new Map(network.vertices.map(vertex => [vertex.id, vertex]));
  const vertexIdByPoint = new Map(network.vertices.map(vertex => [pointKey(vertex), vertex.id]));
  const splitsByEdgeId = new Map<string, Array<{ t: number; point: Point; vertexId: string }>>();
  const sampledEdges = network.edges.flatMap(edge => {
    const from = verticesById.get(edge.from.vertexId);
    const to = verticesById.get(edge.to.vertexId);
    if (!from || !to) {
      return [];
    }

    const samples = sampleEdgeBezier(from, to, edge.from.handle, edge.to.handle, 48);
    return [{ edge, samples, bounds: pointsBounds(samples) }];
  });

  for (let firstIndex = 0; firstIndex < sampledEdges.length; firstIndex += 1) {
    const first = sampledEdges[firstIndex]!;
    for (let secondIndex = firstIndex + 1; secondIndex < sampledEdges.length; secondIndex += 1) {
      const second = sampledEdges[secondIndex]!;
      if (!rectsIntersect(first.bounds, second.bounds)) {
        continue;
      }

      for (let firstSegment = 0; firstSegment < first.samples.length - 1; firstSegment += 1) {
        const firstFrom = first.samples[firstSegment]!;
        const firstTo = first.samples[firstSegment + 1]!;
        for (let secondSegment = 0; secondSegment < second.samples.length - 1; secondSegment += 1) {
          const secondFrom = second.samples[secondSegment]!;
          const secondTo = second.samples[secondSegment + 1]!;
          if (!segmentBoundsIntersect(firstFrom, firstTo, secondFrom, secondTo)) {
            continue;
          }

          const intersection = segmentIntersection(firstFrom, firstTo, secondFrom, secondTo);
          if (!intersection) {
            continue;
          }

          const firstT = (firstSegment + intersection.firstT) / Math.max(1, first.samples.length - 1);
          const secondT = (secondSegment + intersection.secondT) / Math.max(1, second.samples.length - 1);
          if (isEndpointT(firstT) || isEndpointT(secondT)) {
            continue;
          }

          const vertexId = sharedVertexIdForPoint(vertexIdByPoint, intersection.point);
          addSplit(splitsByEdgeId, first.edge.id, { t: firstT, point: intersection.point, vertexId });
          addSplit(splitsByEdgeId, second.edge.id, { t: secondT, point: intersection.point, vertexId });
        }
      }
    }
  }

  if (splitsByEdgeId.size === 0) {
    return structuredClone(network);
  }

  const vertices = network.vertices.map(vertex => ({ ...vertex }));
  const vertexIds = new Set(vertices.map(vertex => vertex.id));
  const edges = network.edges.flatMap(edge => {
    const splits = normalizedSplits(splitsByEdgeId.get(edge.id) ?? []);
    if (splits.length === 0) {
      return [{ ...edge, from: cloneEndpoint(edge.from), to: cloneEndpoint(edge.to) }];
    }

    const splitEdges: VectorEdge[] = [];
    let remainingEdge = { ...edge, from: cloneEndpoint(edge.from), to: cloneEndpoint(edge.to) };
    let previousT = 0;
    for (let index = 0; index < splits.length; index += 1) {
      const split = splits[index]!;
      if (!vertexIds.has(split.vertexId)) {
        vertices.push({ id: split.vertexId, x: split.point.x, y: split.point.y });
        vertexIds.add(split.vertexId);
      }

      const localT = (split.t - previousT) / Math.max(1e-9, 1 - previousT);
      const splitResult = splitEdgeAt(remainingEdge, verticesById, split.vertexId, `${edge.id}:planar:${index}:a`, `${edge.id}:planar:${index}:b`, localT, split.point);
      splitEdges.push(splitResult.firstEdge);
      remainingEdge = splitResult.secondEdge;
      previousT = split.t;
    }

    splitEdges.push({ ...remainingEdge, id: `${edge.id}:planar:${splits.length}` });
    return splitEdges;
  });

  return {
    vertices,
    edges,
    regions: [],
    fillRule: network.fillRule,
    fillOverrideTable: network.fillOverrideTable ? structuredClone(network.fillOverrideTable) : undefined,
  };
}

function addSplit(
  splitsByEdgeId: Map<string, Array<{ t: number; point: Point; vertexId: string }>>,
  edgeId: string,
  split: { t: number; point: Point; vertexId: string },
): void {
  const splits = splitsByEdgeId.get(edgeId) ?? [];
  if (!splits.some(candidate => Math.abs(candidate.t - split.t) <= 0.0001 || candidate.vertexId === split.vertexId)) {
    splits.push(split);
    splitsByEdgeId.set(edgeId, splits);
  }
}

function normalizedSplits(splits: Array<{ t: number; point: Point; vertexId: string }>): Array<{ t: number; point: Point; vertexId: string }> {
  return [...splits]
    .filter(split => !isEndpointT(split.t))
    .sort((left, right) => left.t - right.t)
    .filter((split, index, ordered) => index === 0 || Math.abs(split.t - ordered[index - 1]!.t) > 0.0001);
}

function splitEdgeAt(
  edge: VectorEdge,
  verticesById: Map<string, VectorVertex>,
  vertexId: string,
  firstEdgeId: string,
  secondEdgeId: string,
  t: number,
  fallbackPoint: Point,
): { firstEdge: VectorEdge; secondEdge: VectorEdge } {
  const from = verticesById.get(edge.from.vertexId);
  const to = verticesById.get(edge.to.vertexId);
  if (!from || !to || !edge.from.handle || !edge.to.handle) {
    return {
      firstEdge: { id: firstEdgeId, from: cloneEndpoint(edge.from), to: { vertexId }, directed: edge.directed },
      secondEdge: { id: secondEdgeId, from: { vertexId }, to: cloneEndpoint(edge.to), directed: edge.directed },
    };
  }

  const p01 = lerpPoint(from, edge.from.handle, t);
  const p12 = lerpPoint(edge.from.handle, edge.to.handle, t);
  const p23 = lerpPoint(edge.to.handle, to, t);
  const p012 = lerpPoint(p01, p12, t);
  const p123 = lerpPoint(p12, p23, t);

  verticesById.set(vertexId, { id: vertexId, x: fallbackPoint.x, y: fallbackPoint.y });
  return {
    firstEdge: {
      id: firstEdgeId,
      from: { vertexId: edge.from.vertexId, handle: p01 },
      to: { vertexId, handle: p012 },
      directed: edge.directed,
    },
    secondEdge: {
      id: secondEdgeId,
      from: { vertexId, handle: p123 },
      to: { vertexId: edge.to.vertexId, handle: p23 },
      directed: edge.directed,
    },
  };
}

function sharedVertexIdForPoint(vertexIdByPoint: Map<string, string>, point: Point): string {
  const key = pointKey(point);
  const existing = vertexIdByPoint.get(key);
  if (existing) {
    return existing;
  }

  const nextId = `planar-vertex:${key}`;
  vertexIdByPoint.set(key, nextId);
  return nextId;
}

function cloneEndpoint(endpoint: VectorEdge["from"]): VectorEdge["from"] {
  return {
    vertexId: endpoint.vertexId,
    handle: endpoint.handle ? { ...endpoint.handle } : undefined,
  };
}

function sampleCubic(p0: Point, p1: Point, p2: Point, p3: Point, steps: number): Point[] {
  return Array.from({ length: steps + 1 }, (_, index) => {
    const t = index / steps;
    const mt = 1 - t;
    return {
      x: mt ** 3 * p0.x + 3 * mt ** 2 * t * p1.x + 3 * mt * t ** 2 * p2.x + t ** 3 * p3.x,
      y: mt ** 3 * p0.y + 3 * mt ** 2 * t * p1.y + 3 * mt * t ** 2 * p2.y + t ** 3 * p3.y,
    };
  });
}

function segmentIntersection(
  firstFrom: Point,
  firstTo: Point,
  secondFrom: Point,
  secondTo: Point,
): { point: Point; firstT: number; secondT: number } | undefined {
  const firstDx = firstTo.x - firstFrom.x;
  const firstDy = firstTo.y - firstFrom.y;
  const secondDx = secondTo.x - secondFrom.x;
  const secondDy = secondTo.y - secondFrom.y;
  const denominator = cross(firstDx, firstDy, secondDx, secondDy);
  if (Math.abs(denominator) <= 1e-9) {
    return undefined;
  }

  const deltaX = secondFrom.x - firstFrom.x;
  const deltaY = secondFrom.y - firstFrom.y;
  const firstT = cross(deltaX, deltaY, secondDx, secondDy) / denominator;
  const secondT = cross(deltaX, deltaY, firstDx, firstDy) / denominator;
  if (firstT < -1e-9 || firstT > 1 + 1e-9 || secondT < -1e-9 || secondT > 1 + 1e-9) {
    return undefined;
  }

  const clampedFirstT = clamp01(firstT);
  return {
    point: {
      x: firstFrom.x + clampedFirstT * firstDx,
      y: firstFrom.y + clampedFirstT * firstDy,
    },
    firstT: clampedFirstT,
    secondT: clamp01(secondT),
  };
}

function pointsBounds(points: Point[]): { x: number; y: number; width: number; height: number } {
  const minX = Math.min(...points.map(point => point.x));
  const minY = Math.min(...points.map(point => point.y));
  const maxX = Math.max(...points.map(point => point.x));
  const maxY = Math.max(...points.map(point => point.y));
  return { x: minX, y: minY, width: maxX - minX, height: maxY - minY };
}

function rectsIntersect(
  left: { x: number; y: number; width: number; height: number },
  right: { x: number; y: number; width: number; height: number },
): boolean {
  return (
    left.x <= right.x + right.width &&
    left.x + left.width >= right.x &&
    left.y <= right.y + right.height &&
    left.y + left.height >= right.y
  );
}

function segmentBoundsIntersect(firstFrom: Point, firstTo: Point, secondFrom: Point, secondTo: Point): boolean {
  return rectsIntersect(pointsBounds([firstFrom, firstTo]), pointsBounds([secondFrom, secondTo]));
}

function isEndpointT(t: number): boolean {
  return t <= 0.0001 || t >= 0.9999;
}

function pointKey(point: Point): string {
  return `${roundKey(point.x)},${roundKey(point.y)}`;
}

function roundKey(value: number): string {
  return (Math.round(value * 1000) / 1000).toFixed(3);
}

function cross(ax: number, ay: number, bx: number, by: number): number {
  return ax * by - ay * bx;
}

function lerpPoint(from: Point, to: Point, t: number): Point {
  return {
    x: from.x + (to.x - from.x) * t,
    y: from.y + (to.y - from.y) * t,
  };
}

function clamp01(value: number): number {
  return Math.max(0, Math.min(1, value));
}
